import { format } from 'date-fns';
import type { ResourceTimelineItemColor } from '@all-manager/design-system-v2';
import { usePermissions } from '../../../../hooks/usePermissions';
import type { ActivityTimelineFeed } from '../../../../types';

export type UseActivityTimelineItemProps = {
  item: ActivityTimelineFeed;
  isFirst?: boolean;
  isLast?: boolean;
  showDocuments?: boolean;
  showDetails?: boolean;
};

export function useActivityTimelineItem(props: UseActivityTimelineItemProps) {
  const { isAdmin } = usePermissions();
  const formattedAmount =
    props.item.amount != null
      ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(props.item.amount))
      : null;

  let displayTitle: string;
  switch (props.item.entity_type) {
    case 'event': {
      const eventTitle = props.item.title?.trim() || 'Rendez-vous';
      if (props.item.date) {
        const eventDate = new Date(props.item.date);
        const hideTime = props.item.all_day || format(eventDate, 'HH:mm') === '00:00';
        const dateFormatted = format(eventDate, 'dd/MM/yyyy');
        const timeFormatted = !hideTime ? ` à ${format(eventDate, "HH'h'mm")}` : '';
        displayTitle = `${eventTitle} le ${dateFormatted}${timeFormatted}`;
      } else {
        displayTitle = eventTitle;
      }
      break;
    }

    case 'document': {
      displayTitle = 'Nouveau(x) document(x) importé(s)';
      break;
    }

    case 'incident': {
      const raw = props.item.comment || props.item.title || '';
      const clean = raw.replace(/<[^>]*>/g, '').trim();
      const firstLine = clean.split('\n')[0]?.trim() || '';
      displayTitle = firstLine ? `Incident : ${firstLine}` : 'Incident';
      break;
    }

    case 'quote': {
      const prefix = props.item.status_text === 'approved' ? 'Devis validé' : 'Devis reçu';
      let baseTitle = props.item.title?.trim() || '';
      const rootWord = prefix.split(' ')[0];
      const prefixRegex = new RegExp(`^(${prefix}|${rootWord})\\s*[:-]?\\s*`, 'i');
      if (prefixRegex.test(baseTitle)) {
        baseTitle = baseTitle.replace(prefixRegex, '');
      }
      let title = baseTitle ? `${prefix} : ${baseTitle}` : prefix;
      if (formattedAmount) {
        title += ` (${formattedAmount})`;
      }
      displayTitle = title;
      break;
    }

    case 'other':
    case 'communication':
    default: {
      const prefix =
        props.item.sub_type === 'email'
          ? 'Email'
          : props.item.sub_type === 'reminder'
            ? 'Relance'
            : props.item.sub_type === 'other' || props.item.entity_type === 'other'
              ? 'Autre'
              : 'Appel';
      let baseTitle = props.item.title?.trim() || '';
      const rootWord = prefix.split(' ')[0];
      const prefixRegex = new RegExp(`^(${prefix}|${rootWord})\\s*[:-]?\\s*`, 'i');
      if (prefixRegex.test(baseTitle)) {
        baseTitle = baseTitle.replace(prefixRegex, '');
      }
      displayTitle = baseTitle ? `${prefix} : ${baseTitle}` : prefix;
      break;
    }
  }

  // An exchange (call, e-mail, reminder) reads sender then recipient; other items keep provider / contact.
  const isExchange = props.item.entity_type === 'communication' && props.item.sub_type !== 'other';
  const parts: string[] = [];
  if (isExchange) {
    if (props.item.contact_name) {
      parts.push(`De : ${props.item.contact_name}`);
    }
    if (props.item.provider_name) {
      parts.push(`À : ${props.item.provider_name}`);
    }
  } else {
    if (props.item.provider_name) {
      parts.push(`Prestataire : ${props.item.provider_name}`);
    }
    if (props.item.contact_name) {
      parts.push(`Contact : ${props.item.contact_name}`);
    }
  }
  const subtitle = parts.length > 0 ? parts.join(' • ') : undefined;

  let color: ResourceTimelineItemColor;
  switch (props.item.entity_type) {
    case 'event':
      color = 'rendezVous';
      break;
    case 'incident':
      color = 'incident';
      break;
    case 'document':
    case 'quote':
      color = 'documents';
      break;
    case 'task':
      color = 'task';
      break;
    case 'pending_item':
      color = 'pending_item';
      break;
    case 'communication':
      if (props.item.sub_type === 'reminder') {
        color = 'rappel';
      } else if (props.item.sub_type === 'other') {
        color = 'autre';
      } else {
        color = 'communications';
      }
      break;
    case 'other':
    default:
      color = 'autre';
      break;
  }

  const hasDetails = Boolean(
    props.item.comment ||
    props.showDocuments !== false
  );

  return {
    isAdmin,
    isCommunication: props.item.entity_type === 'communication',
    color,
    displayTitle,
    subtitle,
    hasDetails,
  };
}

